import {
    Dropdown,
    Heading, IconArrowLeft20, IconArrowRight20,
    IconPlane16,
    IconReport16,
    IconSearch24,
    Input,
} from 'pac-ux-components';
import React, {useEffect, useState} from 'react';
import {useCookies} from 'react-cookie';
import { exportTransactions } from './export';
import Loading from '../Loading';

function Transactions(props) {
    const apiRoot = process.env.REACT_APP_API_ROOT_URL;
    const [cookies] = useCookies(['idToken']);
    const apiHeaders = {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + cookies.idToken,
    };

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [transactionId, setTransactionId] = useState('');
    const [endDate, setEndDate] = useState(''); // End date input
    const [startDate, setStartDate] = useState(''); // Start date input
    const [startTime, setStartTime] = useState('00:00');
    const [endTime, setEndTime] = useState('23:59');
    const [currentPage, setCurrentPage] = useState(0); // Page starts from 0
    const [totalPages, setTotalPages] = useState(0);
    const [totalResults, setTotalResults] = useState(0);


    const [hasSearched, setHasSearched] = useState(false);
    const [invoiceId, setInvoiceId] = useState('');
    const [products, setProducts] = useState([]);
    const [merchantProfiles, setMerchantProfiles] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedStore, setSelectedStore] = useState(null);
    const [selectedMerchantProfile, setSelectedMerchantProfile] = useState(
        null);
    const [stores, setStores] = useState([]);
    const [transactions, setTransactions] = useState([]);

    useEffect(() => {
        getProducts();
    }, []);

    const handleSearch = () => {
                // Only search if we have at least one valid search criteria
        if (selectedProduct || selectedStore || selectedMerchantProfile ||
            transactionId.trim() || invoiceId.trim()) {
            setHasSearched(true);
            getTransactions(0);
            setCurrentPage(0); // Reset to first page when search criteria change
        }
    };

    const getTransactions = async (page) => {
        try {
            const searchPayload = createSearchPayload(
                selectedProduct,
                selectedStore,
                selectedMerchantProfile,
                startDate,
                endDate,
                startTime,
                endTime,
                transactionId,
                invoiceId,
                page
            );

            const response = await fetch(
                `${apiRoot}/backoffice/transactions/search`, {
                    method: 'POST',
                    headers: apiHeaders,
                    body: JSON.stringify(searchPayload) });
            if (!response.ok) {
                let errorMessage = 'Failed to fetch transactions';
                const errorBody = await response.json();
                if (errorBody && errorBody.message) {
                    errorMessage = errorBody.message;
                }
                throw new Error(errorMessage);
            }
            const transactionList = await response.json();
            setTransactions(transactionList.content);
            setTotalPages(transactionList.totalPages);
            setTotalResults(transactionList.totalElements);
        } catch (err) {
            setError(err.message || "Failed to fetch transactions");
        }
    };

    const createSearchPayload = (selectedProduct, selectedStore, selectedMerchantProfile, startDate, endDate, startTime, endTime, transactionId = '', invoiceId = '', page=0) => {
        const payload = {};

        if (selectedProduct) {
            payload.productId = selectedProduct.id;
        }
        if (selectedStore) {
            payload.storeId = selectedStore.id;
        }
        if (selectedMerchantProfile) {
            payload.merchantProfileId = selectedMerchantProfile.id;
        }
        if (startDate) {
            const startDateTime = startDate + 'T' + (startTime || '00:00') + ':00';
            payload.startDate = new Date(startDateTime).toISOString();
        }
        if (endDate) {
            const endDateTime = endDate + 'T' + (endTime || '23:59') + ':59';
            payload.endDate = new Date(endDateTime).toISOString();
        }
        if (transactionId.trim()) {
            payload.idempotencyKey = transactionId.trim();
        }
        if (invoiceId.trim()) {
            payload.invoiceId = invoiceId.trim();
        }
        payload.size = 20;
        payload.page = page;
        return payload;
    };

    const getProducts = async () => {
        try {
            const response = await fetch(`${apiRoot}/backoffice/pac-products?size=100&sortBy=name`,
                {headers: apiHeaders});
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const productsList = await response.json();
            console.log('products', productsList.content);
            setProducts(productsList.content);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const productOptions = products.map(
        (product) => ({label: product.name, value: product.id}));

    const storesOptions = stores.map(
        (store) => ({label: store.name, value: store.id}),
    );

    const merchantProfileOptions = merchantProfiles.map(
        (merchant) => ({label: merchant.name, value: merchant.id}),
    );

    const handleDropdownChange = async (e) => {
        const selected = products.find(product => product.id === e);
        setSelectedProduct(selected);
        await getStores(selected.id);
    };

    function handleClickPageLeft() {
        if (currentPage === 1 || transactions.length === 0) {
            return;
        }
        setCurrentPage(currentPage - 1);
        getTransactions(currentPage);
    }

    function handleClickPageRight() {
        if (currentPage === totalPages || transactions.length === 0) {
            return;
        }
        setCurrentPage(currentPage + 1);
        getTransactions(currentPage);
    }

    const handleStoresDropdownChange = async (e) => {
        setSelectedMerchantProfile(null);
        setMerchantProfiles([]);
        const selected = stores.find(store => store.id === e);
        setSelectedStore(selected);
        await getMerchantProfiles(selectedProduct, selected);
    };

    const handleMerchantProfileDropdownChange = async (e) => {
        const selected = merchantProfiles.find(mp => mp.id === e);
        setSelectedMerchantProfile(selected);
    };

    const handlePageChange = (newPage) => {
        setCurrentPage(Number(newPage - 1));
        getTransactions(currentPage);
    };

    const getStores = async (productId) => {
        try {
            const response = await fetch(
                `${apiRoot}/backoffice/pac-products/${productId}/stores?size=100&sortBy=name`,
                {headers: apiHeaders});
            const json = await response.json();

            setStores(json.content);
        } catch (error) {
            console.error('Error fetching stores:', error);
        }
    };

    const getMerchantProfiles = async (product, store) => {
        const response = await fetch(
            apiRoot + '/backoffice/tenants/' + product.id + '/stores/' +
            store.id + '/merchant-profiles?size=100&sortBy=name', {headers: apiHeaders});
        const merchantsData = await response.json();
        setMerchantProfiles(merchantsData.content);
        console.log('merchantsData', merchantsData);
    };

    const handleExportTransactions = async () => {
        try {
            setLoading(true);

            const searchParams = {
                selectedProduct,
                selectedStore,
                selectedMerchantProfile,
                startDate,
                endDate,
                startTime,
                endTime,
                transactionId,
                invoiceId,
                createSearchPayload
            };

            const apiConfig = {
                apiRoot,
                apiHeaders
            };

            await exportTransactions(searchParams, apiConfig);

        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };


    if (loading) return (<Loading/>);
    if (error) return (
        <div style={{
            marginTop: 25,
            marginBottom: 16,
            padding: '12px 16px',
            backgroundColor: '#F5AFAF',
            border: '1px solid rgb(230, 78, 78)',
            borderRadius: '4px',
            fontSize: '14px',
            width: 1047
        }}>
            {error}
        </div>
    );


    return (
        <div>
            <div className="Transactions" style={{width: 1047}}>

                <Heading color={'#10304C'}
                         style={{paddingTop: 96, paddingBottom: 16}}

                >Transactions</Heading>

                <h2>Search for Transactions:</h2>

                <div style={{
                    marginBottom: 16,
                    padding: '12px 16px',
                    backgroundColor: '#d1ecf1',
                    border: '1px solid #bee5eb',
                    borderRadius: '4px',
                    color: '#0c5460',
                    fontSize: '14px',
                    width: 1047
                }}>
                    <strong>Note:</strong> Please enter at least one of: Idempotency key, Invoice (partial) or PAC Product to search.
                </div>

                <div style={{display: 'flex', flexDirection: 'row'}}>

                    <Input
                        style={{
                            marginRight: 16,
                            width: 523,
                            height: 40,
                        }}
                        value={transactionId}
                        placeholder="by Idempotency Key"
                        icon={<IconSearch24
                            style={{marginTop: -2, color: 'black'}}/>}
                        id="by-transaction-id"
                        onChange={(e) => setTransactionId(e.target.value)}
                    />
                    <Input
                        style={{
                            width: 523, height: 40,
                        }}
                        value={invoiceId}
                        placeholder="by Invoice ID"
                        icon={<IconSearch24
                            style={{marginTop: -2, color: 'black'}}/>}
                        id="by-invoice-id"
                        onChange={(e) => setInvoiceId(e.target.value)}
                    />
                </div>
                <div style={{display: 'flex', flexDirection: 'row'}}>
                    {productOptions.length > 0 && (
                        <Dropdown
                            style={{
                                marginTop: 32,
                                marginRight: 16,
                                width: 330,
                            }}
                            label={<div><IconPlane16 style={{marginRight: 8}}/>Select
                                a PAC Product</div>}
                            options={productOptions}
                            selected={selectedProduct}
                            renderSelectedLabel={() => {
                                return <div><IconPlane16
                                    style={{marginRight: 8}}/>{selectedProduct.name}
                                </div>;
                            }}
                            onChange={handleDropdownChange}
                        />
                    )}
                    {stores.length > 0 && selectedProduct && (
                        <Dropdown
                            style={{
                                marginTop: 32,
                                marginRight: 16,
                                width: 330,
                            }}
                            options={storesOptions}
                            selected={selectedStore}
                            onChange={handleStoresDropdownChange}
                            label={<div><img src="/stores.svg" width={16}
                                             height={16} alt="Stores"
                                             style={{marginRight: 8}}/>Select
                                a Store</div>}
                            renderSelectedLabel={() => {
                                return <div><img src="/stores.svg" width={16}
                                                 height={16} alt="Stores"
                                                 style={{marginRight: 8}}/>{selectedStore.name}
                                </div>;
                            }}
                        />
                    )}
                    {selectedStore && (
                        <Dropdown
                            style={{
                                marginTop: 32,
                                marginBottom: 32,
                                width: 330,
                            }}
                            label={<div><IconReport16 style={{marginRight: 8}}/>Select
                                a Merchant Profile</div>}
                            options={merchantProfileOptions}
                            selected={selectedMerchantProfile}
                            onChange={handleMerchantProfileDropdownChange}
                            renderSelectedLabel={() => {
                                return <div><IconReport16
                                    style={{marginRight: 8}}/>{selectedMerchantProfile.name}
                                </div>;
                            }}
                        />
                    )}
                </div>
                <div style={{display: 'flex', flexDirection: 'row'}}>
                    <Input
                        type="date"
                        value={startDate}
                        placeholder="Start Date"
                        onChange={(e) => setStartDate(e.target.value)}
                        style={{
                            marginTop: 32,
                            marginRight: 8,
                            width: 160,
                        }}
                    />
                    <Input
                        type="time"
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                        style={{
                            marginTop: 32,
                            marginRight: 16,
                            width: 120,
                        }}
                    />

                    <Input
                        type="date"
                        value={endDate}
                        placeholder="End Date"
                        onChange={(e) => setEndDate(e.target.value)}
                        style={{
                            marginTop: 32,
                            marginRight: 8,
                            width: 160,
                        }}
                    />
                    <Input
                        type="time"
                        value={endTime}
                        onChange={(e) => setEndTime(e.target.value)}
                        style={{
                            marginTop: 32,
                            marginRight: 16,
                            width: 120,
                        }}
                    />

                    <button
                        onClick={handleSearch}
                        disabled={!selectedProduct && !selectedStore && !selectedMerchantProfile &&
                            !transactionId.trim() && !invoiceId.trim()}
                        style={{
                            marginTop: 32,
                            padding: '10px 20px',
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            height: '40px',
                            opacity: (!selectedProduct && !selectedStore && !selectedMerchantProfile &&
                                !transactionId.trim() && !invoiceId.trim()) ? 0.5 : 1
                        }}
                    >
                        Search
                    </button>

                    <button
                        onClick={handleExportTransactions}
                        disabled={!selectedProduct && !selectedStore && !selectedMerchantProfile &&
                            !transactionId.trim() && !invoiceId.trim()}
                        style={{
                            marginTop: 32,
                            marginLeft: 16,
                            padding: '10px 20px',
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            height: '40px',
                            opacity: (!selectedProduct && !selectedStore && !selectedMerchantProfile &&
                                !transactionId.trim() && !invoiceId.trim()) ? 0.5 : 1
                        }}
                    >
                        Export CSV
                    </button>
                </div>
            </div>

            {hasSearched && transactions.length === 0 && (
                <div style={{
                    marginTop: 32,
                    padding: '20px',
                    textAlign: 'center',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    color: '#6c757d',
                    fontSize: '16px',
                    width: 1047
                }}>
                    No transactions found for the selected criteria.
                </div>
            )}

            {transactions.length > 0 && (
                <table width="1047px" className="Table" style={{marginTop: 32}}>
                    <thead>
                    <tr style={{
                        fontWeight: 600,
                        height: 48,
                        fontSize: 16,
                        fontFamily: 'Inter',
                        border: 'thin solid #E0E0E0',
                    }}>
                        <td style={{paddingLeft: 32, width: 190}}>Idempotency Key</td>
                        <td style={{width: 90}}>Value</td>
                        <td style={{width: 10}}>&nbsp;</td>
                        <td style={{width: 110}}>Payment Method</td>
                        <td style={{width: 190}}>Invoice ID</td>
                        <td style={{width: 100}}>Date</td>
                    </tr>
                    </thead>
                    <tbody>
                    {transactions.map((transaction, index) => (
                        <tr key={transaction.id} style={{
                            height: 48,
                            fontSize: 16,
                            fontWeight: 400,
                            backgroundColor: index % 2 === 0
                                ? '#EDEDED'
                                : '#FFFFFF',
                            border: 'thin solid #E0E0E0',
                        }}>
                            <td style={{paddingLeft: 32}}>
                                <a href={`/transaction/${transaction.idempotencyKey}`}
                                   style={{
                                       textDecoration: 'none',
                                       color: 'inherit',
                                   }}>
                                    {transaction.idempotencyKey}
                                </a>
                            </td>
                            <td>
                                <a href={`/transaction/${transaction.idempotencyKey}`}
                                   style={{
                                       textDecoration: 'none',
                                       color: 'inherit',
                                   }}>
                                    {transaction.amount.value} {transaction.amount.currency}
                                </a>
                            </td>
                            <td>
                                <a href={`/transaction/${transaction.idempotencyKey}`}
                                   title={transaction.status}
                                   style={{
                                       textDecoration: 'none',
                                       color: 'inherit',
                                   }}>
                                    {{
                                        NEW: '🆕',
                                        PROCESSING: '⏳',
                                        AUTHORIZED: '🔐',
                                        DECLINED: '❌',
                                        CAPTURED: '✅',
                                        FAILED: '⚠️',
                                        CANCELLED: '🚫',
                                        USER_CANCELLED: '🚫',
                                        REFUNDED: '💸',
                                        PARTIALLY_REFUNDED: '💸',
                                        REFUND_SUCCESS: '💸',
                                        REFUND_FAILED: '⚠️',
                                        REFUND_INITIATED: '🆕'
                                    }[transaction.status]}
                                </a>
                            </td>
                            <td>
                                <a href={`/transaction/${transaction.idempotencyKey}`}
                                   style={{
                                       textDecoration: 'none',
                                       textTransform: 'capitalize',
                                       color: 'inherit',
                                   }}>
                                    {transaction.paymentProviderName.toLowerCase() !== transaction.paymentMethodName.toLowerCase()
                                        ? `${transaction.paymentProviderName} ${transaction.paymentMethodName}`
                                        : transaction.paymentMethodName}
                                </a>
                            </td>
                            <td>
                                <a href={`/transaction/${transaction.idempotencyKey}`}
                                   style={{
                                       textDecoration: 'none',
                                       color: 'inherit',
                                   }}>
                                    {transaction.invoiceId}
                                </a>
                            </td>
                            <td>
                                <a href={`/transaction/${transaction.idempotencyKey}`}
                                   style={{
                                       textDecoration: 'none',
                                       color: 'inherit',
                                   }}>
                                    {new Intl.DateTimeFormat('en-US', {
                                        dateStyle: 'short',
                                        timeStyle: 'long',
                                    }).format(new Date(transaction.createdAt))}
                                </a>
                            </td>
                        </tr>))}
                    </tbody>
                </table>
            )}
            <div style={{marginTop: 10, width: 1047}}>
        <span>
          {transactions.length > 0 ? `${(currentPage) * 20 + 1} - ${Math.min((currentPage + 1) * 20, totalResults)} of ${totalResults}` : '0 results'}
        </span>
                <span style={{float: 'right'}}>
          Page{' '}

                    <Dropdown
                        options={Array.from({length: totalPages}).map((_, index) => ({
                                value: index + 1, disabled: false, label: index + 1
                            })
                        )}
                        disabled={transactions.length === 0}
                        selected={currentPage + 1}
                        style={{width: 62, marginRight: 4}}
                        onChange={handlePageChange}
                    />
                of {totalPages}

                    <span style={{marginLeft: 32}}>
                    <IconArrowLeft20
                        style={{
                            margin: 8,
                            cursor: (currentPage === 1) ? 'auto' : 'pointer',
                        }}
                        color={currentPage === 1 ? '#ADADAD' : '#000000'}
                        onClick={handleClickPageLeft}
                    />
                    <IconArrowRight20
                        style={{
                            margin: 8,
                            cursor: (currentPage === totalPages || transactions.length === 0) ? 'auto' : 'pointer',
                        }}
                        color={(currentPage === totalPages || transactions.length === 0) ? '#ADADAD' : '#000000'}
                        onClick={handleClickPageRight}
                    />
                </span>
        </span>

            </div>
        </div>
    );
}


export default Transactions;
